/* Browser-only wishlist adapter. Legacy handle storage is retained, never deleted.
   A future account service must migrate with explicit consent, not trust client balances.
   list/has/add/remove remain compatible; entries/replace/removeEntry add variant support. */
(function (root) {
  'use strict';
  const KEY = 'occ:wishlist:v2', LEGACY = 'occ:wishlist';
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(value);
  const variant = value => value == null || value === '' ? null : (/^[1-9]\d*$/.test(String(value)) && Number.isSafeInteger(Number(value)) ? String(value) : undefined);
  const key = entry => entry.handle + ':' + (entry.variantId || 'product');
  function normalise(values) {
    if (!Array.isArray(values)) throw new Error('Invalid wishlist data');
    const seen = new Set();
    return values.flatMap((value, index) => {
      const item = typeof value === 'string' ? {handle:value} : value;
      if (!item || !validHandle(item.handle)) return [];
      const variantId = variant(item.variantId);
      if (variantId === undefined) return [];
      const entry = {handle:item.handle,variantId,savedAt:Number.isFinite(item.savedAt) && item.savedAt >= 0 ? item.savedAt : index};
      if (seen.has(key(entry))) return [];
      seen.add(key(entry));return [entry];
    });
  }
  function create(storage, now = Date.now) {
    let memory = [], transient = false, warning = '';
    const copy = list => list.map(entry => ({...entry}));
    function read() {
      if (transient) return copy(memory);
      try {
        const current = storage.getItem(KEY);
        if (current !== null) memory = normalise(JSON.parse(current));
        else memory = normalise(JSON.parse(storage.getItem(LEGACY) || '[]'));
        warning = '';
      } catch (error) {
        // Never replace corrupt/unreadable data with an empty persisted list.
        transient = true;
        warning = 'Browser storage is unavailable or unreadable. Changes will last only while this page stays open.';
        if (!memory.length) {
          try { memory = normalise(JSON.parse(storage.getItem(LEGACY) || '[]')); } catch (_) { /* retain last good in-memory entries */ }
        }
      }
      return copy(memory);
    }
    function write(entries) {
      memory = normalise(entries);
      if (!transient) {
        try { storage.setItem(KEY, JSON.stringify(memory));warning = ''; }
        catch (_) { transient = true;warning = 'Browser storage is full or blocked. Changes will last only while this page stays open.'; }
      }
      return {ok:true,persisted:!transient,warning};
    }
    function add(handle, variantId = null) {
      const value = normalise([{handle,variantId,savedAt:now()}])[0];
      if (!value) return {ok:false,warning:'This product or option cannot be saved.'};
      const entries = read();
      if (entries.some(entry=>key(entry)===key(value))) return {ok:true,persisted:!transient,warning};
      if (entries.length >= 200) return {ok:false,warning:'Your wishlist has reached 200 entries. Remove an item before saving another.'};
      return write([...entries,value]);
    }
    return {
      key, keys:[KEY,LEGACY], entries:read,
      list:()=>[...new Set(read().map(entry=>entry.handle))],
      has:handle=>read().some(entry=>entry.handle===handle),
      add, remove:handle=>write(read().filter(entry=>entry.handle!==handle)),
      removeEntry:entryKey=>write(read().filter(entry=>key(entry)!==entryKey)),
      replace(entryKey, variantId) {
        const entries = read(), old = entries.find(entry=>key(entry)===entryKey), id = variant(variantId);
        if (!old || id === undefined) return {ok:false,warning:'The saved item changed. Refresh the list and try again.'};
        const replacement = {...old,variantId:id};
        return write(entries.map(entry=>key(entry)===entryKey?replacement:entry));
      },
      restore(entry) {
        const entries = read();
        return write(entries.some(item=>key(item)===key(entry))?entries:[...entries,entry]);
      },
      warning:()=>warning,
      // Re-read successful persistent changes made in another tab. An unsaved
      // in-memory session is not discarded by another tab's storage event.
      refresh:()=>read(),
    };
  }
  root.OCCWishlistAdapter = {create,key,normalise,validHandle};
})(typeof window === 'undefined' ? globalThis : window);

